import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { useIntl } from 'hooks/Intl';
import {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import { medicineService } from 'services';
import { CountryOption, getCountryOptions } from 'utils/medicine';

type MedicineContext = {
  availableCountries: string[];
  availableCountriesStatus: LoadingStatus;
  countryOptions: CountryOption[];
};

export const IntlContext = createContext<MedicineContext>({
  availableCountries: [],
  availableCountriesStatus: 'NOT_LOADED' as LoadingStatus,
  countryOptions: [],
});

const MedicineProvider = ({ children }: { children: ReactNode }) => {
  const { idiom } = useIntl();

  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [availableCountriesStatus, setAvailableCountriesStatus] = useState(
    'NOT_LOADED' as LoadingStatus
  );

  const fetchCountries = async () => {
    try {
      setAvailableCountriesStatus?.('LOADING');
      const { data: countries } = await medicineService.getCountries();
      setAvailableCountries(countries);
      setAvailableCountriesStatus?.('LOADED');
    } catch (error) {
      console.log(error);
      setAvailableCountriesStatus?.('ERROR');
    }
  };

  const countryOptions = useMemo(
    () => getCountryOptions(availableCountries, idiom),
    [availableCountries, idiom]
  );

  useEffect(() => {
    fetchCountries();
  }, []);

  return (
    <IntlContext.Provider
      value={{
        availableCountries,
        availableCountriesStatus,
        countryOptions,
      }}
    >
      {availableCountriesStatus === 'LOADED' ? (
        children
      ) : (
        <LoadingSpinner fill />
      )}
    </IntlContext.Provider>
  );
};

export const useMedicine = () => useContext(IntlContext);

export default MedicineProvider;
